import { useState } from 'react';
import { type Kitchen, NoteType } from '@rta/rta-blueprint-library';

import type { BlueprintPage } from '../types';
import {
	REVISION_ADD_ROW_BUTTON_STYLE,
	REVISION_BODY_STYLE,
	REVISION_COL_DATE_STYLE,
	REVISION_COL_DESC_STYLE,
	REVISION_COL_ENG_STYLE,
	REVISION_COL_REV_STYLE,
	REVISION_DATA_CELL_STYLE,
	REVISION_HEADER_CELL_STYLE,
	REVISION_TABLE_STYLE
} from './blueprint-revision-log-page.styles';
import { BlueprintPageFooter } from '../shared/blueprint-page-footer';
import { BlueprintPageHeader } from '../shared/blueprint-page-header';
import { BlueprintPageWrapper } from '../shared/blueprint-page-wrapper';
import { EditableText } from '../shared/editable-text';
import { formatBlueprintDate } from '../helpers/format-blueprint-date';
import { overrideToPlainText } from '../shared/sanitize-override-html';
import { REVISION_ROW_COUNT, stampRevisionRows } from '../helpers/stamp-blueprint-dates';

export type BlueprintRevisionLogPageProps = {
	page: BlueprintPage;
	kitchen: Kitchen;
	designerInitials?: string;
	currentUserInitials?: string;
	customerName?: string;
	layoutDescription?: string;
	designerName?: string;
	creationDate?: string;
	threeDImageUrl?: string;
};

type RevisionRow = {
	rev: string;
	date: string;
	description: string;
	engineer: string;
};

export const BlueprintRevisionLogPage = (props: BlueprintRevisionLogPageProps): React.ReactElement => {
	const {
		page,
		kitchen,
		designerInitials = '',
		currentUserInitials = '',
		customerName,
		layoutDescription,
		designerName,
		creationDate,
		threeDImageUrl
	} = props;

	const [extraRows, setExtraRows] = useState((): number => parseInt(kitchen.blueprintOverrides?.['revision.extraRows'] ?? '', 10) || 0);
	const [isHovered, setIsHovered] = useState(false);
	// Bumped on cell blur so a just-stamped engineer override renders (the override event is excluded from the container subscription to protect the caret).
	const [, setRefreshTick] = useState(0);

	const handleAddRow = (): void => {
		const next = extraRows + 1;
		setExtraRows(next);
		kitchen.setBlueprintOverride('revision.extraRows', next.toString());
	};

	// Build revision rows
	const rows: Array<RevisionRow> = [];

	// First row is always the initial creation — the header's own date, which survives a new version
	// (kitchen.createDate is the draft's date and reset this row to today on every revision, #416).
	rows.push({
		rev: '-',
		date: creationDate || formatBlueprintDate(kitchen.createDate),
		description: 'Initial BP creation',
		engineer: designerInitials
	});

	// Additional rows from kitchen notes (revision type)
	const revisionNotes = kitchen.notes?.children
		.filter((note) => note.type === NoteType.Revision)
		.sort((a, b) => {
			const dateA = a.createDate ? new Date(a.createDate).getTime() : 0;
			const dateB = b.createDate ? new Date(b.createDate).getTime() : 0;
			return dateA - dateB;
		}) ?? [];

	revisionNotes.forEach((note, index) => {
		rows.push({
			rev: (index + 1).toString(),
			date: formatBlueprintDate(note.createDate as unknown as string),
			description: note.message ?? '',
			engineer: note.createdBy ? note.createdBy.split(' ').map((n) => n.charAt(0)).join('') : designerInitials
		});
	});

	// Pad to REVISION_ROW_COUNT plus user-added rows; the engineer cell is stamped into blueprintOverrides when a Rev is typed, so blame stays with the author, not the viewer.
	while (rows.length < REVISION_ROW_COUNT + extraRows) {
		rows.push({ rev: '', date: '', description: '', engineer: '' });
	}

	// Initials land on a row the moment it is filled in, so blame stays with the author rather than the
	// next person to open the sheet. The date is not written here — only an export commits one.
	const stampRowMetadata = (): void => stampRevisionRows({ kitchen, currentUserInitials });

	// A revision's date is the day its document was issued, so a dateless row shows today live until an
	// export freezes it — and only once its Rev cell holds something, never on an untouched row.
	const todayFormatted = formatBlueprintDate(new Date());
	const overrides = kitchen.blueprintOverrides ?? {};
	for (let index = 1 + revisionNotes.length; index < rows.length; index++) {
		if (rows[index].date) continue;
		if (!overrideToPlainText(overrides[`revision.${index}.rev`] ?? '').trim()) continue;
		rows[index] = { ...rows[index], date: todayFormatted };
	}

	return (
		<BlueprintPageWrapper pageNumber={page.pageNumber}>
			<BlueprintPageHeader
				title={page.title}
				pageNumber={page.pageNumber}
				customerName={customerName}
				layoutDescription={layoutDescription}
				designerName={designerName}
				creationDate={creationDate}
				threeDImageUrl={threeDImageUrl}
				showThumbnail={true}
			/>

			<div style={REVISION_BODY_STYLE} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} onInput={stampRowMetadata} onBlur={() => { stampRowMetadata(); setRefreshTick((tick) => tick + 1); }}>
				<table style={REVISION_TABLE_STYLE}>
					<colgroup>
						<col style={REVISION_COL_REV_STYLE} />
						<col style={REVISION_COL_DATE_STYLE} />
						<col style={REVISION_COL_DESC_STYLE} />
						<col style={REVISION_COL_ENG_STYLE} />
					</colgroup>
					<thead>
						<tr>
							<th style={REVISION_HEADER_CELL_STYLE}>Rev</th>
							<th style={REVISION_HEADER_CELL_STYLE}>Date</th>
							<th style={REVISION_HEADER_CELL_STYLE}>Description</th>
							<th style={REVISION_HEADER_CELL_STYLE}>Engineer</th>
						</tr>
					</thead>
					<tbody>
						{rows.map((row, index) => {
							const rowBg = index % 2 === 0 ? 'white' : '#f5f5f5';
							const borderColor = index === 0 ? '#ccc' : '#eee';
							return (
								<tr key={index} style={{ backgroundColor: rowBg, borderBottom: `1px solid ${borderColor}` }}>
									<EditableText overrideKey={`revision.${index}.rev`} element='td' style={REVISION_DATA_CELL_STYLE}>{row.rev}</EditableText>
									<EditableText overrideKey={`revision.${index}.date`} element='td' style={REVISION_DATA_CELL_STYLE}>{row.date}</EditableText>
									<EditableText overrideKey={`revision.${index}.description`} element='td' style={REVISION_DATA_CELL_STYLE}>{row.description}</EditableText>
									<EditableText overrideKey={`revision.${index}.engineer`} element='td' style={REVISION_DATA_CELL_STYLE}>{row.engineer}</EditableText>
								</tr>
							);
						})}
					</tbody>
				</table>
				<button
					data-html2canvas-ignore="true"
					onClick={handleAddRow}
					style={{ ...REVISION_ADD_ROW_BUTTON_STYLE, opacity: isHovered ? 1 : 0, pointerEvents: isHovered ? 'auto' : 'none' }}
				>
					+ Add Row
				</button>
			</div>

			<BlueprintPageFooter pageNumber={page.pageNumber} />
		</BlueprintPageWrapper>
	);
};
